import type { CallTerminalRecord } from '../call/callState';
import {
  parseCallRecordEvent,
  type CallRoomParticipants,
} from '../call/callProtocol';

export type ChatMessageKind = 'text' | 'image' | 'voice' | 'call';
export type ChatMessageSendStatus = 'pending' | 'sent' | 'failed';

export interface ChatMessage {
  eventId: string;
  receiptEventId?: string;
  kind: ChatMessageKind;
  senderId: string;
  timestamp: number;
  sendStatus: ChatMessageSendStatus;
  body: string;
  mediaMxc?: string;
  thumbnailMxc?: string;
  mimeType?: string;
  size?: number;
  width?: number;
  height?: number;
  durationMs?: number;
  waveform?: number[];
  isVoice?: boolean;
  call?: CallTerminalRecord;
}

export type CallMessageTranslationKey =
  | 'commsCallCompleted'
  | 'commsCallDeclined'
  | 'commsCallCancelled'
  | 'commsCallMissed'
  | 'commsCallNoAnswer'
  | 'commsCallBusy'
  | 'commsCallRecordFailed';

export type CallMessageTone = 'success' | 'attention' | 'busy' | 'neutral';

export interface CallMessagePresentation {
  key: CallMessageTranslationKey;
  tone: CallMessageTone;
  values?: { duration: string };
}

export interface MatrixMessageEvent {
  getId(): string | undefined;
  getType(): string;
  getSender(): string | undefined;
  getTs(): number;
  getContent(): Record<string, unknown>;
  status?: string | null;
}

const stringValue = (value: unknown) =>
  typeof value === 'string' ? value : undefined;

const numberValue = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const objectValue = (value: unknown) =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const sendStatus = (status?: string | null): ChatMessageSendStatus => {
  if (status === 'not_sent' || status === 'cancelled') return 'failed';
  if (status == null || status === 'sent') return 'sent';
  return 'pending';
};

export const formatCallDuration = (durationSec: number) => {
  const seconds = Math.max(0, Math.floor(durationSec));
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(
    seconds % 60,
  ).padStart(2, '0')}`;
};

export const getCallMessagePresentation = (
  message: ChatMessage,
  matrixUserId: string | null | undefined,
): CallMessagePresentation | null => {
  const call = message.call;
  if (message.kind !== 'call' || !call) return null;
  if (call.outcome === 'completed')
    return {
      key: 'commsCallCompleted',
      tone: 'success',
      values: { duration: formatCallDuration(call.durationSec ?? 0) },
    };
  if (call.outcome === 'declined')
    return { key: 'commsCallDeclined', tone: 'neutral' };
  if (call.outcome === 'cancelled')
    return { key: 'commsCallCancelled', tone: 'neutral' };
  if (call.outcome === 'missed')
    return {
      key:
        matrixUserId === call.initiator
          ? 'commsCallNoAnswer'
          : 'commsCallMissed',
      tone: 'attention',
    };
  if (call.outcome === 'busy') return { key: 'commsCallBusy', tone: 'busy' };
  return { key: 'commsCallRecordFailed', tone: 'attention' };
};

export const projectChatMessage = (
  event: MatrixMessageEvent,
  participants?: CallRoomParticipants,
): ChatMessage | null => {
  const eventId = event.getId();
  const senderId = event.getSender();
  if (!eventId || !senderId) return null;

  const content = event.getContent();
  if (event.getType() === 'org.gba.call') {
    if (!participants) return null;
    const call = parseCallRecordEvent(
      content,
      senderId,
      event.getTs(),
      participants,
    );
    if (!call) return null;
    return {
      eventId,
      kind: 'call',
      senderId,
      timestamp: event.getTs(),
      sendStatus: sendStatus(event.status),
      body: '',
      call,
    };
  }
  if (event.getType() !== 'm.room.message') return null;
  const msgtype = stringValue(content.msgtype);
  const body = stringValue(content.body);
  if (!body) return null;
  const common = {
    eventId,
    senderId,
    timestamp: event.getTs(),
    sendStatus: sendStatus(event.status),
    body,
  };

  if (msgtype === 'm.text') return { ...common, kind: 'text' };

  const mediaMxc = stringValue(content.url);
  if (!mediaMxc) return null;
  const info = objectValue(content.info);
  if (msgtype === 'm.image') {
    return {
      ...common,
      kind: 'image',
      mediaMxc,
      thumbnailMxc: stringValue(info.thumbnail_url),
      mimeType: stringValue(info.mimetype),
      size: numberValue(info.size),
      width: numberValue(info.w),
      height: numberValue(info.h),
    };
  }
  if (msgtype !== 'm.audio') return null;

  const audio = objectValue(content['org.matrix.msc1767.audio']);
  const waveform = Array.isArray(audio.waveform)
    ? audio.waveform.filter(
        (value): value is number =>
          typeof value === 'number' && Number.isFinite(value),
      )
    : undefined;
  return {
    ...common,
    kind: 'voice',
    mediaMxc,
    mimeType: stringValue(info.mimetype),
    size: numberValue(info.size),
    durationMs: numberValue(audio.duration),
    waveform,
    isVoice: Boolean(content['org.matrix.msc3245.voice']),
  };
};
